import { useCallback } from "react";
import { useLocalStorage } from "usehooks-ts";

export interface RecentlyVisitedPage {
  id: string;
  title: string;
  type: string;
}

// Maximum number of items to store in local storage
export const MAX_RECENT_ITEMS = 20;

// Default number of items to show in the UI
export const CONFIG_MAX_VISIBLE_ITEMS = 3;

/**
 * Hook to manage the recently visited pages.
 * We store the current page in the list, but we don't show it in the list until the next page is added.
 *
 * This unfortunately doesn't use URLs, but a basic object with an id, title, and type.
 */
export function useRecentlyVisited(): [
  RecentlyVisitedPage[] | undefined,
  (item: RecentlyVisitedPage) => void,
] {
  const [recent, setRecent] = useLocalStorage<RecentlyVisitedPage[]>("cbh.recently-visited", []);

  const push = useCallback(
    (item: RecentlyVisitedPage) => {
      setRecent((previousRecent) => {
        const existing = previousRecent.find((existingItem) => existingItem.id === item.id);
        if (existing) {
          // if you switch to an existing item, move it to the start of the array
          return [item, ...previousRecent.filter((recentItem) => recentItem.id !== item.id)].slice(
            0,
            MAX_RECENT_ITEMS
          );
        }

        return [item, ...previousRecent].slice(0, MAX_RECENT_ITEMS);
      });
    },
    [setRecent]
  );

  return [recent, push];
}
